import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ErrorBox } from "@/components/common";
import { AuthShell } from "@/components/AuthShell";
import { useApp } from "@/lib/store";
import { parseApiError } from "@/lib/api/client";
import { Building2, CheckCircle2, Sparkles } from "lucide-react";

interface SignupResponse {
  token: string;
  user: { id: number; username: string; email: string };
  organization: { id: string; name: string; slug: string; schema_name: string };
  workspace: { id: string; name: string; slug: string;
               organization?: string; organization_slug?: string;
               organization_name?: string };
  default_workspace_id?: string;
  default_tenant_schema?: string;
  tenant_url: string;
  studio_url: string;
}

interface EmailCheck {
  valid: boolean;
  is_disposable: boolean;
  is_free: boolean;
  is_company: boolean;
  blocked: boolean;
  reason: string;
}

const INDUSTRIES = [
  { id: "fsi",       label: "Financial Services & Insurance", pack: "acme-fsi-pack" },
  { id: "health",    label: "Healthcare & Life Sciences",     pack: "acme-health-pack" },
  { id: "industry",  label: "Manufacturing / Industrial",     pack: "acme-industry-pack" },
  { id: "public",    label: "Public Sector",                  pack: "acme-public-pack" },
  { id: "retail",    label: "Retail & E-commerce",            pack: "acme-retail-pack" },
  { id: "custom",    label: "Custom / Skip for now",          pack: null },
];

const API_BASE = (import.meta.env.VITE_API_BASE_URL || "/api/v1").replace(/\/+$/, "");

function slugify(s: string): string {
  return s.toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

export function Signup() {
  const nav = useNavigate();
  const setAuth = useApp((s) => s.setAuth);
  const setWorkspaces = useApp((s) => s.setWorkspaces);
  const setWorkspace = useApp((s) => s.setWorkspace);

  const [orgName, setOrgName] = useState("");
  const [orgSlug, setOrgSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [industry, setIndustry] = useState("fsi");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<SignupResponse | null>(null);

  // Live, best-effort email classification (valid / disposable / free /
  // company). Purely advisory — the backend re-validates on submit. Debounced
  // so we don't probe on every keystroke.
  const [emailHint, setEmailHint] = useState<EmailCheck | null>(null);
  useEffect(() => {
    const v = email.trim();
    if (!v || !v.includes("@") || !v.includes(".")) { setEmailHint(null); return; }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API_BASE}/iam/email/validate/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: v }),
          signal: ctrl.signal,
        });
        if (r.ok) setEmailHint((await r.json()) as EmailCheck);
      } catch { /* network/abort — stay silent, submit will validate */ }
    }, 450);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [email]);

  const computedSlug = useMemo(
    () => (slugTouched ? orgSlug : slugify(orgName)),
    [orgName, orgSlug, slugTouched]);

  const passwordOk = password.length >= 12;
  // Compute *why* the form can't be submitted so we can show it inline
  // instead of silently disabling the primary action button. The previous
  // version used `disabled={!canSubmit}` with no visible cue, so a user
  // with a 10-char password thought "Create tenant" was un-wired.
  const missing: string[] = [];
  if (!orgName) missing.push("organization name");
  if (!computedSlug) missing.push("URL slug");
  if (!email) missing.push("email");
  if (!username) missing.push("username");
  if (!passwordOk) missing.push("password (min 12 chars)");
  const canSubmit = missing.length === 0 && !busy;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) {
      setError(`Please complete: ${missing.join(", ")}.`);
      return;
    }
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/iam/signup/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          organization_name: orgName,
          organization_slug: computedSlug,
          industry,
          full_name: fullName,
          email, username, password,
          workspace_name: "Default workspace",
        }),
      });
      if (!r.ok) {
        throw await parseApiError(r);
      }
      const data = (await r.json()) as SignupResponse;
      setAuth(data.token, { ...data.user });
      // Seed the workspaces list with the freshly-provisioned default
      // workspace and pin the user to it. This stamps X-Tenant-Schema
      // for subsequent API calls so the new tenant's schema is the
      // active one immediately — no manual workspace switching needed.
      const ws = {
        ...data.workspace,
        organization: data.organization.id,
        organization_slug: data.organization.slug,
        organization_name: data.organization.name,
      };
      setWorkspaces([ws]);
      setWorkspace(ws.id);
      // Persist the tenant schema explicitly in case setWorkspace's
      // lookup races against the freshly-stored workspaces array.
      localStorage.setItem("ckos.tenantSchema", data.organization.schema_name);
      setDone(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  if (done) {
    const ind = INDUSTRIES.find((i) => i.id === industry);
    return (
      <div className="mx-auto max-w-2xl">
        <div className="mb-5">
          <h1 className="text-2xl font-semibold tracking-tight">Welcome aboard</h1>
          <p className="mt-1 text-sm text-muted">Your tenant is provisioned and your account is ready.</p>
        </div>
        <div className="card">
          <div className="mb-4 flex items-center gap-2 text-good">
            <CheckCircle2 size={20} /> <span className="font-medium">Tenant created.</span>
          </div>
          <div className="mb-4 rounded-lg border border-border bg-panel px-3 py-2 text-[12px] text-muted">
            We've emailed a verification link to <strong>{done.user.email}</strong>.
            Confirm it to secure your account — you can keep exploring meanwhile.
          </div>
          <dl className="grid grid-cols-[140px_1fr] gap-y-2 text-sm">
            <dt className="text-muted">Organization</dt>
            <dd>{done.organization.name} <code className="text-xs text-muted">({done.organization.slug})</code></dd>
            <dt className="text-muted">Schema</dt>
            <dd><code className="text-xs">{done.organization.schema_name}</code></dd>
            <dt className="text-muted">Workspace</dt>
            <dd>{done.workspace.name}</dd>
            <dt className="text-muted">Tenant URL</dt>
            <dd><a className="text-accent underline" href={done.tenant_url}>{done.tenant_url}</a></dd>
            {ind?.pack && (
              <>
                <dt className="text-muted">Domain pack</dt>
                <dd className="flex items-center gap-1">
                  <Sparkles size={14} className="text-accent" /> {ind.label}
                  <span className="ml-1 text-xs text-muted">({ind.pack} — install from Packs)</span>
                </dd>
              </>
            )}
          </dl>
          <div className="mt-5 flex gap-2">
            <button className="btn-primary" onClick={() => nav({ to: "/dashboard" })}>
              Open Studio →
            </button>
            <Link to="/packs" className="btn">Browse domain packs</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthShell
      eyebrow="Get started"
      title="Stand up your tenant in one step"
      subtitle="We provision your organization, schema, default workspace and admin user — isolated and governed from the first login."
      bullets={[
        "Schema-per-tenant isolation, materialized automatically",
        "A starter domain pack matched to your industry",
        "BYO-LLM keys, encrypted per workspace",
      ]}
    >
      <div className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight">Create your tenant</h1>
        <p className="mt-1 text-sm text-muted">
          One form. We provision your organization, schema, default workspace, and admin user.
        </p>
      </div>
      <form onSubmit={submit}>
        <div className="mb-3 flex items-center gap-2 text-sm font-medium">
          <Building2 size={16} className="text-accent" /> Organization
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="label" htmlFor="su-org">Organization name</label>
            <input id="su-org" className="input" autoFocus
                   value={orgName} onChange={(e) => setOrgName(e.target.value)} />
          </div>
          <div>
            <label className="label" htmlFor="su-slug">URL slug</label>
            <input id="su-slug" className="input"
                   value={computedSlug}
                   onChange={(e) => { setOrgSlug(slugify(e.target.value)); setSlugTouched(true); }} />
            <div className="mt-1 text-[11px] text-muted">
              Tenant URL: <code>{computedSlug || "your-slug"}.localhost:8000</code>
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="label" htmlFor="su-ind">Industry preset</label>
            <select id="su-ind" className="input"
                    value={industry} onChange={(e) => setIndustry(e.target.value)}>
              {INDUSTRIES.map((i) => (
                <option key={i.id} value={i.id}>{i.label}</option>
              ))}
            </select>
            <div className="mt-1 text-[11px] text-muted">
              We'll suggest a starter Domain Pack on first login.
            </div>
          </div>
        </div>

        <div className="mt-4 mb-3 text-sm font-medium">Admin user</div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="label" htmlFor="su-name">Full name</label>
            <input id="su-name" className="input"
                   value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div>
            <label className="label" htmlFor="su-email">Email</label>
            <input id="su-email" className="input" type="email"
                   value={email} onChange={(e) => setEmail(e.target.value)} />
            {emailHint && (
              <div className={`mt-1 text-[11px] ${
                emailHint.blocked ? "text-bad"
                : emailHint.is_company ? "text-good" : "text-muted"}`}>
                {emailHint.blocked
                  ? emailHint.reason
                  : emailHint.is_company
                    ? "Company email — looks good."
                    : "Tip: a work email speeds up team onboarding."}
              </div>
            )}
          </div>
          <div>
            <label className="label" htmlFor="su-user">Username</label>
            <input id="su-user" className="input"
                   value={username} onChange={(e) => setUsername(e.target.value)} />
          </div>
          <div>
            <label className="label" htmlFor="su-pwd">Password</label>
            <input id="su-pwd" className="input" type="password"
                   value={password} onChange={(e) => setPassword(e.target.value)} />
            <div className={`mt-1 text-[11px] ${passwordOk ? "text-good" : "text-muted"}`}>
              {passwordOk ? "Strong enough" : "Min 12 characters"}
            </div>
          </div>
        </div>

        {error && <div className="mt-3"><ErrorBox error={error} /></div>}
        {!error && missing.length > 0 && (
          <div className="mt-3 text-[11px] text-muted">
            Still needed: {missing.join(", ")}.
          </div>
        )}

        <div className="mt-5 flex items-center justify-between">
          <Link to="/login" className="text-xs text-muted hover:text-accent">
            ← Already have an account? Sign in
          </Link>
          {/* Intentionally NOT disabled — clicking with an incomplete form
              surfaces a clear error message instead of doing nothing. */}
          <button className={`btn-primary ${canSubmit ? "" : "opacity-60"}`}
                  type="submit" aria-disabled={!canSubmit}>
            {busy ? "Provisioning…" : "Create tenant"}
          </button>
        </div>
      </form>
    </AuthShell>
  );
}
